/**
 * SAVA Size Collision Popup
 *
 * Watches `cart:update` for a second, different size of the same product
 * landing in the cart (from either the PDP add-to-cart form or the
 * collection-card quick-add) and, when that happens, suppresses the cart
 * drawer and shows a bottom-sheet dialog instead, offering to keep the new
 * size, keep the previous size, or keep both.
 *
 * Plain script, no ES module imports (loaded as a classic deferred script).
 * Reuses the existing `<dialog-component>` (assets/dialog.js) for open/close
 * behavior, and `/cart/change.js` for line removal.
 */
(function () {
  if (window.__savaSizeCollisionInit) return;
  window.__savaSizeCollisionInit = true;

  var SELF_SOURCE = 'sava-size-collision';
  var ALLOWED_SOURCES = ['product-form-component', 'sava-quick-add'];
  var SIZE_NAME_RE = /größe|grösse|groesse|size/i;

  /** @type {Array<{key:string, product_id:number, variant_id:number, quantity:number}>|null} */
  var previousCartItems = null;
  var cartInitPromise = null;

  // ---- Helpers ----

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function snapshot(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    return cart.items.map(function (item) {
      return {
        key: item.key,
        product_id: item.product_id,
        variant_id: item.variant_id,
        quantity: item.quantity,
      };
    });
  }

  // Prime a baseline snapshot as early as possible so the first real
  // add-to-cart event has something to diff against.
  function primeSnapshot() {
    if (cartInitPromise) return cartInitPromise;
    cartInitPromise = fetchCart()
      .then(function (cart) {
        previousCartItems = snapshot(cart);
      })
      .catch(function () {
        previousCartItems = previousCartItems || [];
      });
    return cartInitPromise;
  }
  primeSnapshot();

  /**
   * Finds the line that was just added by diffing against the previous
   * snapshot. Only a brand-new line key counts as "just added" - bumping the
   * quantity of an already-existing line (e.g. adding the same size again)
   * is intentionally NOT treated as a new add, so it can never trigger the
   * collision popup.
   */
  function findAddedLine(oldItems, newItems) {
    var oldByKey = {};
    oldItems.forEach(function (item) {
      oldByKey[item.key] = item;
    });

    var newestNewLine = null;
    newItems.forEach(function (item) {
      if (!oldByKey[item.key] && item.quantity > 0) {
        newestNewLine = item;
      }
    });
    return newestNewLine;
  }

  function normalizeSizeValue(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  /** @returns {string|null} the size option value, or null if no size-named option exists */
  function getSizeValue(item) {
    var opts = (item && item.options_with_values) || [];
    for (var i = 0; i < opts.length; i++) {
      if (opts[i] && SIZE_NAME_RE.test(opts[i].name || '')) {
        return opts[i].value;
      }
    }
    return null;
  }

  function getDisplayLabel(item) {
    var size = getSizeValue(item);
    if (size) return size;
    var opts = (item && item.options_with_values) || [];
    if (opts.length) return opts.map(function (o) { return o.value; }).join(' / ');
    return item && item.variant_title ? item.variant_title : '';
  }

  function imageUrl(item) {
    if (!item) return '';
    if (item.featured_image && item.featured_image.url) return item.featured_image.url;
    if (typeof item.image === 'string' && item.image) return item.image;
    return '';
  }

  /**
   * Given the freshly-added cart line, finds the sibling line (same product,
   * different variant) that makes this a size collision - or null if this
   * isn't one.
   */
  function findCollisionCandidate(cartItems, addedItem) {
    var newSizeRaw = getSizeValue(addedItem);
    var hasSizeOption = newSizeRaw !== null;
    var normalizedNewSize = normalizeSizeValue(newSizeRaw);

    var siblings = cartItems.filter(function (it) {
      return it.product_id === addedItem.product_id && it.variant_id !== addedItem.variant_id;
    });

    if (siblings.length === 0) return null;

    if (hasSizeOption) {
      // Only a collision if a sibling differs specifically in the size option.
      return (
        siblings.filter(function (it) {
          var siblingSize = getSizeValue(it);
          return siblingSize !== null && normalizeSizeValue(siblingSize) !== normalizedNewSize;
        })[0] || null
      );
    }

    // No size-named option on this product at all - fall back to treating any
    // other variant of the same product as a collision.
    return siblings[0];
  }

  function removeLine(key) {
    var url = (window.Theme && Theme.routes && Theme.routes.cart_change_url) || '/cart/change.js';
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    }).then(function (r) {
      if (!r.ok) throw new Error('cart/change request failed');
      return r.json();
    });
  }

  function broadcastCartUpdate(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cart, data: { source: SELF_SOURCE } },
      })
    );
  }

  function closeCartDrawer() {
    var attempt = function () {
      var drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.close === 'function') drawer.close();
    };
    // Try immediately...
    attempt();
    // ...and once more a couple of frames later. cart-drawer.js opens its
    // auto-open drawer via a single requestAnimationFrame, so if our handler
    // ran before that frame executed, the first attempt() above would find
    // the dialog not yet open (a no-op). A double rAF guarantees this second
    // attempt runs strictly after that frame, so it reliably closes it.
    requestAnimationFrame(function () {
      requestAnimationFrame(attempt);
    });
  }

  function getDialogEl() {
    return document.getElementById('sava-size-collision');
  }

  function closePopup() {
    var dialog = getDialogEl();
    if (dialog && typeof dialog.closeDialog === 'function') dialog.closeDialog();
  }

  function openPopup(newItem, oldItem) {
    var dialog = getDialogEl();
    if (!dialog || typeof dialog.showDialog !== 'function') return;

    var imageEl = dialog.querySelector('[data-sava-sc-image]');
    var titleEl = dialog.querySelector('[data-sava-sc-title]');
    var sizesEl = dialog.querySelector('[data-sava-sc-sizes]');
    var keepNewBtn = dialog.querySelector('[data-sava-sc-keep-new]');
    var keepOldBtn = dialog.querySelector('[data-sava-sc-keep-old]');
    var keepBothBtn = dialog.querySelector('[data-sava-sc-keep-both]');
    var closeBtn = dialog.querySelector('[data-sava-sc-close]');

    var newLabel = getDisplayLabel(newItem);
    var oldLabel = getDisplayLabel(oldItem);
    var title = newItem.product_title || newItem.title || '';
    var img = imageUrl(newItem) || imageUrl(oldItem);

    if (imageEl) {
      if (img) {
        imageEl.src = img;
        imageEl.alt = title;
        imageEl.hidden = false;
      } else {
        imageEl.removeAttribute('src');
        imageEl.hidden = true;
      }
    }
    if (titleEl) titleEl.textContent = title;
    if (sizesEl) sizesEl.textContent = 'Im Warenkorb: Größe ' + oldLabel + ' und Größe ' + newLabel;
    if (keepNewBtn) keepNewBtn.textContent = 'Nur Größe ' + newLabel + ' behalten';
    if (keepOldBtn) keepOldBtn.textContent = 'Nur Größe ' + oldLabel + ' behalten';

    function cleanup() {
      if (keepNewBtn) keepNewBtn.removeEventListener('click', onKeepNew);
      if (keepOldBtn) keepOldBtn.removeEventListener('click', onKeepOld);
      if (keepBothBtn) keepBothBtn.removeEventListener('click', onKeepBoth);
      if (closeBtn) closeBtn.removeEventListener('click', onKeepBoth);
    }

    function afterRemoval() {
      return fetchCart().then(function (cart) {
        previousCartItems = snapshot(cart);
        broadcastCartUpdate(cart);
      });
    }

    function onKeepNew() {
      cleanup();
      closePopup();
      removeLine(oldItem.key)
        .then(afterRemoval)
        .catch(function (err) {
          console.error('SAVA size collision: failed to remove previous size', err);
        });
    }

    function onKeepOld() {
      cleanup();
      closePopup();
      removeLine(newItem.key)
        .then(afterRemoval)
        .catch(function (err) {
          console.error('SAVA size collision: failed to remove new size', err);
        });
    }

    function onKeepBoth() {
      cleanup();
      closePopup();
    }

    if (keepNewBtn) keepNewBtn.addEventListener('click', onKeepNew);
    if (keepOldBtn) keepOldBtn.addEventListener('click', onKeepOld);
    if (keepBothBtn) keepBothBtn.addEventListener('click', onKeepBoth);
    if (closeBtn) closeBtn.addEventListener('click', onKeepBoth);

    dialog.showDialog();
  }

  // ---- Main handler ----

  function handleCartUpdate(e) {
    try {
      var src = e.detail && e.detail.data && e.detail.data.source;

      // Ignore updates we dispatched ourselves (guards against re-entrancy /
      // infinite loops when we remove a line and re-broadcast cart:update).
      if (src === SELF_SOURCE) return;
      // Only the two add-to-cart paths can create a size collision.
      if (ALLOWED_SOURCES.indexOf(src) === -1) return;
      // NB: intentionally no "isProcessing" drop here. A single add can emit two
      // cart:update events in the same tick; dropping the second one would miss
      // the collision. Re-entrancy from our own line removal is already prevented
      // by the SELF_SOURCE filter above, so processing every allowed event is safe.

      var resource = e.detail && e.detail.resource;
      var cartPromise =
        resource && Array.isArray(resource.items) ? Promise.resolve(resource) : fetchCart();

      Promise.resolve(cartInitPromise)
        .catch(function () {})
        .then(function () {
          return cartPromise;
        })
        .then(function (cart) {
          var newItems = snapshot(cart);
          var oldItems = previousCartItems || [];
          var addedLine = findAddedLine(oldItems, newItems);

          // Always refresh the baseline for the next diff, regardless of outcome.
          previousCartItems = newItems;

          if (!addedLine) return; // no brand-new line (e.g. just a qty bump) -> never a collision

          var addedFullItem = cart.items.filter(function (it) {
            return it.key === addedLine.key;
          })[0];
          if (!addedFullItem) return;

          var candidate = findCollisionCandidate(cart.items, addedFullItem);
          if (!candidate) return;

          // Collision confirmed: suppress the cart drawer and show the popup instead.
          closeCartDrawer();
          openPopup(addedFullItem, candidate);
        })
        .catch(function (err) {
          console.error('SAVA size collision: handler failed', err);
        });
    } catch (err) {
      // Never let this feature break the normal add-to-cart flow.
      console.error('SAVA size collision: unexpected error', err);
    }
  }

  document.addEventListener('cart:update', handleCartUpdate);
})();
